import * as Sentry from '@sentry/react';
import i18n from 'i18next';
import { ApiError } from '@/api/http-client';

interface ValidationErrorPayload {
	message?: string;
	details?: Record<string, string[]>;
}

interface MessagePayload {
	message?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isMessagePayload(value: unknown): value is MessagePayload {
	return isRecord(value) && (value.message === undefined || typeof value.message === 'string');
}

function isValidationErrorPayload(value: unknown): value is ValidationErrorPayload {
	if (!isRecord(value)) {
		return false;
	}

	if (value.message !== undefined && typeof value.message !== 'string') {
		return false;
	}

	if (value.details === undefined) {
		return true;
	}

	if (!isRecord(value.details)) {
		return false;
	}

	return Object.values(value.details).every(
		(messages) => Array.isArray(messages) && messages.every((message) => typeof message === 'string'),
	);
}

export function isExpectedApiError(error: unknown): boolean {
	return error instanceof ApiError && error.status >= 400 && error.status < 500;
}

export function reportErrorToSentry(error: unknown, componentStack?: string): void {
	if (isExpectedApiError(error)) {
		return;
	}

	Sentry.captureException(error, {
		contexts: {
			react: { componentStack },
		},
	});
}

export function isNotFoundError(error: unknown): boolean {
	return error instanceof ApiError && error.status === 404;
}

export function getApiErrorMessage(error: unknown, fallback: string = i18n.t('error_generic')): string {
	if (error instanceof ApiError) {
		if (typeof error.data === 'string' && error.data.length > 0) {
			return error.data;
		}

		if (isMessagePayload(error.data) && error.data.message) {
			return error.data.message;
		}

		return `${error.status} ${error.statusText}`;
	}

	if (error instanceof Error && error.message.length > 0) {
		return error.message;
	}

	return fallback;
}

export function getValidationDetails(error: unknown): Record<string, string[]> | null {
	if (!(error instanceof ApiError)) {
		return null;
	}

	if (!isValidationErrorPayload(error.data)) {
		return null;
	}

	return error.data.details ?? null;
}
